import axios from "axios";
import { getApiErrorCode, getApiErrorStatus } from "@/utils/apiError";

/** One entry of the 400 body from POST /registration/submit; `field` is a backend path such as `address[0].place`. */
export interface RegistrationValidationError {
  type: string;
  description: string;
  field: string;
}

export interface MappedFieldError {
  /** react-hook-form path, e.g. `partialCompany.address.0.place` */
  name: string;
  /** i18n key */
  message: string;
}

export type SubmitErrorOutcome =
  | { kind: "validation"; fieldErrors: MappedFieldError[]; unmapped: RegistrationValidationError[] }
  | { kind: "orgNumberTaken" }
  | { kind: "alreadySubmitted" }
  | { kind: "companyTypeNotAllowed" }
  | { kind: "unknown" };

const MESSAGE_KEY_BY_TYPE: Record<string, string> = {
  NULL: "validation:required",
  EMPTY: "validation:required",
  WRONG_LENGTH: "validation:wrongLength",
  WRONG_EMAIL_FORMAT: "validation:emailFormatError",
  INVALID_TIMEZONE: "validation:invalidTimeZone",
  IS_TRUE: "validation:termsRequired",
  TOO_SMALL: "validation:mustBeGreaterThanZero",
  BELOW_MINIMUM: "validation:cannotBeNegative",
  INVALID: "validation:invalidValue",
};

// Top-level properties of the registration form; errors on anything else can't be shown on a field.
const FORM_FIELDS = new Set([
  "name",
  "email",
  "vatNumber",
  "leiNumber",
  "eoriNumber",
  "phone",
  "address",
  "countryCodeIso3",
  "languageId",
  "bankAccount",
  "defaultCurrency",
  "companyProvidesTransport",
  "companyType",
  "timeZone",
  "admin",
  "teamMembers",
  "membershipType",
  "terms",
  "creditLimit",
  "invoiceDaysBeforeDueDate",
  "settlementDays",
]);

export const isRegistrationValidationErrorList = (body: unknown): body is RegistrationValidationError[] =>
  Array.isArray(body) &&
  body.every(
    (item) => !!item && typeof item === "object" && typeof item.field === "string" && typeof item.type === "string"
  );

export const toFormPath = (field: string) => `partialCompany.${field.replace(/\[(\d+)\]/g, ".$1")}`;

export const mapRegistrationErrors = (errors: RegistrationValidationError[]) => {
  const fieldErrors: MappedFieldError[] = [];
  const unmapped: RegistrationValidationError[] = [];
  for (const error of errors) {
    if (!FORM_FIELDS.has(error.field.split(/[.[]/)[0])) {
      unmapped.push(error);
      continue;
    }
    fieldErrors.push({
      name: toFormPath(error.field),
      message: MESSAGE_KEY_BY_TYPE[error.type] ?? "validation:invalidValue",
    });
  }
  return { fieldErrors, unmapped };
};

export const classifySubmitError = (error: unknown): SubmitErrorOutcome => {
  const body = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (getApiErrorStatus(error) === 400 && isRegistrationValidationErrorList(body)) {
    return { kind: "validation", ...mapRegistrationErrors(body) };
  }
  switch (getApiErrorCode(error)) {
    case "USR-232":
      return { kind: "orgNumberTaken" };
    case "USR-250":
      return { kind: "alreadySubmitted" };
    case "USR-253":
      return { kind: "companyTypeNotAllowed" };
    default:
      return { kind: "unknown" };
  }
};
